import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import { useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";

type FeatureFlag = 
  | "gymEnabled" 
  | "waitlistEnabled" 
  | "smsEnabled" 
  | "selfServiceEnabled" 
  | "blockCoursesEnabled";

interface FeatureGuardProps {
  feature: FeatureFlag;
  children: React.ReactNode;
  /** What to render when feature is disabled. Defaults to redirect to home. */
  fallback?: "redirect" | "hide" | React.ReactNode;
  /** Custom redirect path. Defaults to "/" */
  redirectTo?: string;
}

/**
 * Guard component that checks if a feature is enabled before rendering children.
 * 
 * Usage:
 * ```tsx
 * <FeatureGuard feature="gymEnabled">
 *   <GymPage />
 * </FeatureGuard>
 * 
 * <FeatureGuard feature="smsEnabled" fallback="hide">
 *   <SmsConsentCheckbox />
 * </FeatureGuard>
 * ```
 */
export function FeatureGuard({ 
  feature, 
  children, 
  fallback = "redirect",
  redirectTo = "/"
}: FeatureGuardProps) {
  const { data: config, isLoading } = trpc.config.getTenantConfig.useQuery();
  const [, setLocation] = useLocation();

  const isEnabled = config?.features?.[feature] ?? false;

  useEffect(() => {
    if (!isLoading && !isEnabled && fallback === "redirect") {
      setLocation(redirectTo);
    }
  }, [isLoading, isEnabled, fallback, redirectTo, setLocation]);

  // Show loading skeleton while checking
  if (isLoading) {
    if (fallback === "hide") {
      return null;
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Skeleton className="h-32 w-32" />
      </div>
    );
  }

  // Feature is disabled
  if (!isEnabled) {
    if (fallback === "redirect") {
      // Will redirect via useEffect
      return null;
    }
    if (fallback === "hide") {
      return null;
    }
    // Custom fallback component
    return <>{fallback}</>;
  }

  // Feature is enabled, render children
  return <>{children}</>;
}

/**
 * Hook to check if a feature is enabled
 */
export function useFeatureEnabled(feature: FeatureFlag): { enabled: boolean; loading: boolean } {
  const { data: config, isLoading } = trpc.config.getTenantConfig.useQuery();
  
  return {
    enabled: config?.features?.[feature] ?? false,
    loading: isLoading,
  };
}

/**
 * Hook to get all feature flags
 */
export function useFeatures() {
  const { data: config, isLoading } = trpc.config.getTenantConfig.useQuery();
  
  return {
    features: config?.features ?? {
      gymEnabled: false,
      waitlistEnabled: true,
      smsEnabled: false,
      selfServiceEnabled: true,
      blockCoursesEnabled: false,
    },
    loading: isLoading,
  };
}
